/**
 * Экран жизни. Режим `turn` (по умолчанию, core-loop-v2): год — ход из
 * четырёх тактов на одном экране. Шапка: имя · страна, год, энергия
 * (`EnergyBar`), автопилот. Центр: возраст и состояния; затем по такту —
 * утро года (`MorningCard`: дельты, записи, фокус, «К делам») или меню
 * мест (`PlacesMenu`); решение/мини-игра/предложение мечты (`DreamPicker`)
 * показываются в любом такте, закрывают «Следующий год» (`NextYearButton`,
 * всегда внизу колонки) и подсвечены (`attention`, Review Focus 3);
 * мечта с прогрессом — в шапке (`DreamLine`), цель года — строкой под
 * состояниями (`GoalLine`); кнопка-имя открывает «Личное дело» (H1);
 * ниже — журнал. Часов в этом режиме нет вовсе (`clock === null`): год
 * идёт только по кнопке или по автопилоту, который листает годы сам
 * (AUTOPILOT_MS), пока включён.
 *
 * Режим `observe` (core-loop-v2 §7, Task 7) — единственный, где живут часы
 * реального времени: шапка с управлением скоростью, лента фокусов,
 * подсказки; энергию за игрока тратит политика движка (стор). Часы создаются
 * при входе на экран и останавливаются при уходе с него, поэтому на экранах
 * старта и рождения год не идёт. Для завершённой жизни часы не запускаются,
 * а страница неподвижна (`.still` — все анимации отключены безусловно).
 * Предложенная мечта держит часы, как решение, но при автопилоте — нет:
 * тик берёт первую мечту сам (иначе часы встали бы навсегда).
 *
 * Мини-игра (план 2b): открытая `game.pendingMinigame` рисуется каркасом
 * `Host` на месте карточки решения и, как решение, останавливает часы;
 * карточка решения показывается только когда мини-игры нет (автопилот,
 * «пропускать тяжёлое», решение без мини-игры).
 */
import { getValue, type Dataset } from '@life/data';
import type { Life } from '@life/engine';
import { t, yearsWord } from '@life/i18n';
import { minigameDef } from '@life/minigames';
import { effect } from '@preact/signals';
import { useEffect, useMemo } from 'preact/hooks';
import { DecisionCard } from '../components/DecisionCard';
import { DreamLine, DreamPicker } from '../components/DreamPicker';
import { EnergyBar } from '../components/EnergyBar';
import { Envelope } from '../components/Envelope';
import { FocusRibbon } from '../components/FocusRibbon';
import { GoalLine } from '../components/GoalLine';
import { Hints } from '../components/Hints';
import { Journal } from '../components/Journal';
import { LifeMap } from '../components/LifeMap';
import { MorningCard } from '../components/MorningCard';
import { NextYearButton, AUTO_REPEAT_MS } from '../components/NextYearButton';
import { PlacesMenu } from '../components/PlacesMenu';
import { SpeedControl } from '../components/SpeedControl';
import { Stats } from '../components/Stats';
import { ThemeToggle } from '../components/ThemeToggle';
import { useMediaQuery } from '../lib/media';
import { Host } from '../minigames/Host';
import { createClock, yearMs, type Clock } from '../state/clock';
import type { Game } from '../state/game';
import { isHelpMinigame, minigameRng } from '../state/minigame';

/** Брейкпоинт одной колонки (DESIGN-SYSTEM.md). */
export const NARROW_QUERY = '(max-width: 760px)';
/** Автопилот в режиме хода: год за этот интервал (как автоповтор кнопки). */
export const AUTOPILOT_MS = AUTO_REPEAT_MS;

export interface LifeScreenProps {
  game: Game;
  ds: Dataset;
  /** Часы режима «Наблюдать»; по умолчанию создаются на экране (инъекция — для тестов). В режиме хода не используются. */
  clock?: Clock;
}

/** Предложенная мечта ждёт выбора: держит год, как решение. */
function dreamWaits(life: Life | null): boolean {
  return life !== null && life.pendingDream !== null && life.pendingDream.length > 0;
}

function lifeExpectancy(ds: Dataset, life: Life): number {
  return getValue(ds, life.iso3, life.sex === 'f' ? 'lifeExpF' : 'lifeExpM', life.birthYear).v;
}

export function LifeScreen({ game, ds, clock: injected }: LifeScreenProps) {
  const life = game.life.value;
  const narrow = useMediaQuery(NARROW_QUERY);
  const turn = game.settings.value.mode === 'turn';
  // Часы — только в «Наблюдать»; в режиме хода их нет, даже если переданы снаружи.
  const clock = useMemo<Clock | null>(() => {
    if (turn) return null;
    return (
      injected ??
      createClock({
        yearMs: () => {
          const current = game.life.value;
          return yearMs(game.settings.value.durationMin, current ? lifeExpectancy(ds, current) : NaN);
        },
        onYear: () => game.tick(),
        hasPending: () => game.pending.value !== null || game.pendingMinigame.value !== null || dreamWaits(game.life.value),
        speed: game.settings.value.speed,
      })
    );
  }, [turn, injected, game, ds]);
  const alive = life?.alive ?? false;
  const pending = game.pending.value;
  const minigame = game.pendingMinigame.value;
  const dreamPending = dreamWaits(life);
  const autopilot = game.autopilot.value;
  const paused = pending !== null || minigame !== null || (dreamPending && !autopilot);
  const phase = game.phase.value;

  useEffect(() => {
    if (!alive || clock === null) return;
    clock.start();
    return () => clock.stop();
  }, [clock, alive]);

  // Режим хода: автопилот листает годы сам, пока включён (решения и мини-игры он проходит по ожиданию).
  useEffect(() => {
    if (!alive || !turn || !autopilot) return;
    const id = setInterval(() => {
      game.endYear();
    }, AUTOPILOT_MS);
    return () => clearInterval(id);
  }, [game, alive, turn, autopilot]);

  // Скорость — настройка: выбранное ×N переживает перезагрузку.
  useEffect(() => {
    if (clock === null) return;
    return effect(() => {
      const speed = clock.speed.value;
      if (game.settings.peek().speed !== speed) game.settings.value = { ...game.settings.peek(), speed };
    });
  }, [clock, game]);

  // Решение — остановка: пока оно ждёт ответа, год не идёт; после ответа
  // часы продолжают, если их не остановил сам игрок.
  useEffect(() => {
    if (!alive || clock === null || !paused) return;
    const pausedByPlayer = clock.paused.value;
    clock.pause();
    return () => {
      if (!pausedByPlayer) clock.resume();
    };
  }, [clock, alive, paused]);

  if (life === null) return null;

  const country = ds.countries.find((c) => c.iso3 === life.iso3)?.nameRu ?? life.iso3;

  const side = (
    <>
      {/* На узком экране заголовок уже в ручке шторки. */}
      {!narrow && <div class="eyebrow">{t('ui.life.map')}</div>}
      <LifeMap life={life} />
      <Envelope
        life={life}
        onPayDebt={(sum) => game.payDebt(sum)}
        minigames={alive ? game.minigamesAvailable({ kind: 'envelope' }) : []}
        busy={minigame !== null || game.minigameQueued.value !== null}
        onOpen={(id) => game.openMinigame({ kind: 'envelope' }, id)}
      />
      {!narrow && (
        <div class="side-foot">
          <ThemeToggle game={game} />
        </div>
      )}
    </>
  );

  return (
    <main class={alive ? 'page life-page' : 'page life-page still'}>
      <header class="topbar">
        {alive ? (
          <button type="button" class="btn ghost name" aria-label={t('ui.dossier.open', { name: life.name })} onClick={() => game.openDossier()}>
            <b>{t('ui.life.header', { name: life.name, country })}</b>
          </button>
        ) : (
          <b>{t('ui.life.header', { name: life.name, country })}</b>
        )}
        <div class="topbar-tools">
          <span class="mono">{life.birthYear + life.age}</span>
          {alive && <DreamLine life={life} ds={ds} />}
          {turn && alive && <EnergyBar life={life} />}
          {clock !== null && alive && <SpeedControl clock={clock} compact={narrow} pending={pending !== null} />}
          {clock !== null && !alive && <span class="chip">{t('ui.life.speed', { n: clock.speed.value })}</span>}
          {alive && (
            <button
              type="button"
              class="btn"
              aria-pressed={autopilot}
              onClick={() => (game.autopilot.value = !autopilot)}
            >
              {t('ui.life.autopilot')}
            </button>
          )}
        </div>
      </header>
      <div class="life">
        <div class="main">
          <div class="age">
            {life.age}
            <small>{yearsWord(life.age)}</small>
          </div>
          <Stats stats={life.stats} />
          {alive && <GoalLine life={life} />}
          {(!turn || !alive) && (
            <div class="focus-line">
              <span class="eyebrow">{t('ui.life.focus')}</span>
              {!alive && <span>{t(`focus.${life.focus}`)}</span>}
            </div>
          )}
          {clock !== null && alive && <Hints game={game} clock={clock} />}
          {!turn && alive && (
            <FocusRibbon
              life={life}
              focus={game.focus.value}
              focusChanges={game.focusChanges.value}
              onFocus={(f) => game.setFocus(f)}
            />
          )}
          {alive && minigame !== null && (
            <Host
              key={`${minigame.id}@${minigame.openedAt}`}
              def={minigameDef(minigame.id)}
              state={minigame.state}
              life={life}
              rng={minigameRng(life, minigame.id).fork('play')}
              heavy={isHelpMinigame(minigame.id)}
              skipHeavy={game.settings.value.skipHeavy}
              onDone={(outcome) => game.finishMinigame(outcome)}
            />
          )}
          {alive && minigame === null && pending !== null && (
            <div class={turn && !autopilot ? 'attention' : ''}>
              <DecisionCard decision={pending} autopilot={autopilot} onAnswer={(id) => game.answer(id)} />
            </div>
          )}
          {alive && minigame === null && pending === null && dreamPending && <DreamPicker game={game} attention={!autopilot} />}
          {turn && alive && phase === 'morning' && <MorningCard game={game} />}
          {turn && alive && phase !== 'morning' && <PlacesMenu game={game} ds={ds} />}
          {turn && alive && <NextYearButton game={game} />}
          <Journal
            life={life}
            onStar={(age) => game.starYear(age)}
            onThought={(age, text) => game.addThought(age, text)}
          />
        </div>
        {narrow ? (
          <>
            {/* На узком экране переключатель темы виден сразу, не внутри шторки. */}
            <div class="side-foot">
              <ThemeToggle game={game} />
            </div>
            <details class="side">
              <summary>{t('ui.life.side')}</summary>
              {side}
            </details>
          </>
        ) : (
          <aside class="side">{side}</aside>
        )}
      </div>
    </main>
  );
}
